import { RootState } from 'store';
import { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import noImagePlaceholderSrc from 'assets/images/no-image-placeholder.svg';

import { getFinishEffectsByColor } from 'order/wizard/orderStyles/productLines/store/specifications/orderStylesSpecificationActions';
import { StyleSpecifications } from 'order/wizard/orderStyles/interface/StyleSpecifications';

import { nevada } from 'shared/config/Colors';
import { P } from 'shared/components/Typography';
import { SelectOptionProps } from 'shared/interface/SelectOptionProps';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import Img from 'shared/components/Img';
import Loader, { LoaderFullScreen } from 'shared/components/Loader';
import ScrollbarsCustom from 'shared/components/ScrollbarsCustom';
import UtilService from 'shared/services/util.service';
import WarningTooltip from 'shared/components/WarningTooltip';
import Scrollbars from 'react-custom-scrollbars';
import { Wrapper } from 'shared/components/Wrapper';
import FormError from 'shared/components/FormError';
import { useHasPermissions } from 'shared/hooks/useHasPermissions';
import { userPermissionsValues } from 'shared/enum/userPermissionsEnum';
import { zeroGuid } from 'shared/config/Variables';
import { ProductLineEnums } from 'order/enums/ProductLineEnums';
import { SpecificationsOptionsEnum } from 'order/wizard/orderStyles/enums/SpecificationsOptionsEnum';
import OrderFinishLabel from 'order/components/OrderFinishLabel/OrderFinishLabel';
import { Select } from 'shared/components/Select';
import { FormLabel } from 'shared/components/FormLabel';
import DesignerFinishToggle from '../DesignerFinishToggle/DesignerFinishToggle';

const FinishColorContainer = styled.div`
  display: grid;
  gap: 14px 7px;
  grid-template-columns: repeat(5, 1fr);
  padding: 10px;
  text-align: center;
`;

const FinishColorLabel = styled.div`
  color: ${({ theme }) => theme.nevada};
  font-size: 12px;
  margin: 8px auto 0 auto;
  max-width: 96px;
  text-align: center;
  word-wrap: break-word;
`;

const FinishColorEl = styled.div<{ disabled: boolean }>`
  /* border-radius: 50%; */
  position: relative;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0.4;
  `}

  &::before {
    border-radius: 50%;
    content: '';
    height: 100px;
    left: 50%;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: -2px;
    transform: translateX(-50%) scale(1.1);
    transition: 300ms ease;
    width: 100px;
    box-shadow: inset 0 0 0 3px ${({ theme }) => theme.kashmirBlue},
      inset 0 0 0 6px white;
  }

  &.selected {
    &::before {
      opacity: 1;
      transform: translateX(-50%) scale(1);
    }

    ${FinishColorLabel} {
      color: ${({ theme }) => theme.kashmirBlue};
      font-weight: 600;
    }
  }
`;

const FinishColorImg = styled(Img)`
  border-radius: 50%;
  display: block;
  margin: 0 auto;
`;

interface FinishColorsProps {
  isFinishColorsLoading?: boolean;
  setIsFinishColorsLoading?: (isLoading: boolean) => void;
}

const FinishColor: FC<FinishColorsProps> = ({
  isFinishColorsLoading,
  setIsFinishColorsLoading, // eslint-disable-line @typescript-eslint/no-unused-vars
}) => {
  const dispatch = useAppDispatch();

  const [finishColorScrollbarsRef, setFinishColorScrollbarsRef] =
    useState<Scrollbars | null>(null);

  const { watch, setValue, control, clearErrors, formState, register } =
    useFormContext<StyleSpecifications>();

  const woodOrMaterialWatched = watch('woodOrMaterial');
  const finishColorWatched = watch('finishColor');
  const specialFinishSampleWatched = watch('specialFinishSample');
  const showDesignerFinishesWatched = watch('showDesignerFinishes');
  const showAvailableFinishesOnlyWatched = watch('showAvailableFinishesOnly');

  const hasSpecialFinishSample = specialFinishSampleWatched?.length > 0;
  const previousFinishColorOptionsLengthRef = useRef<number | null>(null);
  const lastResetFinishColorRef = useRef<string | null>(null);
  const curateAutoSelectRef = useRef(false);

  const selectedProductLine = useSelector(
    (state: RootState) => state.orderStylesReducer.productLine
  );

  const allFinishColorOptions: SelectOptionProps[] | null | undefined =
    useSelector(
      (state: RootState) =>
        state.orderStylesReducer.specificationsOptions.finishColorOptions
    );

  const materialColorOptions = useSelector(
    (state: RootState) =>
      state.orderStylesReducer.specificationsOptions.materialColorOptions
  );

  const hasDesignerFinishes = useSelector(
    (state: RootState) =>
      state.orderStylesReducer.specificationsOptions.hasDesignerFinishes ??
      false
  );

  const finishColorOptions = useMemo(() => {
    if (!allFinishColorOptions) return null;

    // Helper to check boolean values that might be strings
    const isTrue = (value: boolean | string | undefined): boolean =>
      value === true || value === 'true';

    const designerList = allFinishColorOptions.filter((option) =>
      isTrue(option.designerFinish)
    );

    const nonDesignerList = allFinishColorOptions.filter(
      (option) => !isTrue(option.designerFinish)
    );

    if (showDesignerFinishesWatched) {
      return designerList;
    }

    // If Special Finish Sample # has a value, ignore 'standard' filtering
    // When checkbox is checked, show ALL items (including designerFinish=true) where availableOnly=true
    if (hasSpecialFinishSample) {
      if (showAvailableFinishesOnlyWatched) {
        // Include both designer and non-designer finishes where availableOnly=true
        return allFinishColorOptions.filter((option) =>
          isTrue(option.availableOnly)
        );
      }
      return nonDesignerList;
    }

    // No special finish sample:
    // - If checkbox checked (availableOnly=true): show only availableOnly=true
    // - If checkbox not checked (availableOnly=false): show only standard=true
    if (showAvailableFinishesOnlyWatched) {
      return nonDesignerList.filter((option) => isTrue(option.availableOnly));
    }

    return nonDesignerList.filter((option) => isTrue(option.standard));
  }, [
    allFinishColorOptions,
    hasSpecialFinishSample,
    showDesignerFinishesWatched,
    showAvailableFinishesOnlyWatched,
  ]);

  const onlyOneOption = (finishColorOptions?.length ?? 0) === 1;

  const storedFinishColor = useSelector(
    (state: RootState) => state.orderStylesReducer.specifications?.finishColor
  );

  const storedFirstFinishEffect = useSelector((state: RootState) =>
    state.orderStylesReducer.specifications?.finishEffects?.find(
      (fe) => fe.orderNumber === 1
    )
  );

  const storedWoodOrMaterial = useSelector(
    (state: RootState) =>
      state.orderStylesReducer.specifications?.woodOrMaterial ??
      state.orderStylesReducer.specifications?.materialGroup?.woodMaterialId
  );

  const isICSProductLine = useSelector(
    (state: RootState) =>
      state.orderStylesReducer.productLine?.name ===
      ProductLineEnums.PRODUCT_LINE_ICS
  );

  const isPreludeProductLine = useSelector(
    (state: RootState) =>
      state.orderStylesReducer.productLine?.name ===
      ProductLineEnums.PRODUCT_LINE_PRELUDE
  );

  const isUserDealerOrSalesRep = useHasPermissions([
    userPermissionsValues.DEALER_VIEW_ORDERBOARD,
    userPermissionsValues.SALES_REPRESENTATIVE_VIEW_ORDER_BOARD,
  ]);

  const isUserCSR = useHasPermissions(
    userPermissionsValues.CSR_VIEW_ORDERBOARD
  );

  const canEdit = useSelector((state: RootState) => state.orderReducer.canEdit);

  const handleDesignerFinishToggle = (isDesignerFinish: boolean) => {
    setValue('finishColor', null, { shouldDirty: true });
    setValue('finishColorUpcharge', '', { shouldDirty: true });
    setValue('showDesignerFinishes', isDesignerFinish, { shouldDirty: true });
    lastResetFinishColorRef.current = null;
  };

  const getFinishEffects = (finishColorOption: SelectOptionProps | null) => {
    dispatch(
      getFinishEffectsByColor({
        productLineId: selectedProductLine!.id,
        woodMaterialId: woodOrMaterialWatched!.value,
        finishColorId: finishColorOption?.value ?? zeroGuid,
        specialFinishSample: specialFinishSampleWatched,
        showOnlyAvailable: formState.dirtyFields.specialFinishSample
          ? hasSpecialFinishSample
          : storedFirstFinishEffect?.showAvailableOnly ?? false,
      })
    );
  };

  const onFinishColorChangeHandler = (
    finishColorOption: SelectOptionProps | null,
    shouldDirty: boolean
  ) => {
    if (selectedProductLine && woodOrMaterialWatched && finishColorOption) {
      setValue('finishColor', finishColorOption, {
        shouldDirty,
      });
      clearErrors('finishColor');

      UtilService.withDecimal<StyleSpecifications>(
        'finishColorUpcharge',
        finishColorOption.upcharge!.toString(),
        setValue
      );

      if (shouldDirty) {
        getFinishEffects(finishColorOption);
      }
    }
  };

  const finishColorReset = (shouldDirty: boolean) => {
    setValue('finishColor', null, { shouldDirty });
    setValue('finishColorUpcharge', '', { shouldDirty });
    setValue('showAvailableFinishesOnly', false, { shouldDirty });
  };

  useEffect(() => {
    if (isFinishColorsLoading) {
      finishColorScrollbarsRef?.scrollToTop();
    }
  }, [isFinishColorsLoading]);

  useEffect(() => {
    if (storedFinishColor && !formState.isDirty) {
      onFinishColorChangeHandler(storedFinishColor, false);
    }
  }, [storedFinishColor]);

  useEffect(() => {
    const isWoodOrMaterialGroupDirty = UtilService.dirtyOrTouchedFields(
      formState,
      ['woodOrMaterial', 'materialGroup']
    );

    if (
      isUserDealerOrSalesRep !== undefined &&
      isUserDealerOrSalesRep &&
      isWoodOrMaterialGroupDirty &&
      finishColorOptions &&
      woodOrMaterialWatched &&
      onlyOneOption
    ) {
      onFinishColorChangeHandler(finishColorOptions[0], true);
    }
  }, [isUserDealerOrSalesRep, onlyOneOption, finishColorOptions]);

  useEffect(() => {
    if (
      isUserCSR &&
      onlyOneOption &&
      finishColorOptions &&
      woodOrMaterialWatched
    ) {
      getFinishEffects(finishColorWatched);
    }
  }, [isUserCSR, onlyOneOption, finishColorOptions]);

  useEffect(() => {
    if (!storedWoodOrMaterial) {
      finishColorReset(false);
    }

    if (storedWoodOrMaterial && formState.dirtyFields.woodOrMaterial) {
      finishColorReset(false);
    }
  }, [storedWoodOrMaterial, woodOrMaterialWatched]);

  useEffect(() => {
    if (
      isICSProductLine &&
      formState.isDirty &&
      materialColorOptions &&
      materialColorOptions.length > 0
    ) {
      curateAutoSelectRef.current = true;
    }
  }, [materialColorOptions]);

  useEffect(() => {
    if (
      curateAutoSelectRef.current &&
      finishColorOptions &&
      finishColorOptions.length > 0 &&
      !finishColorWatched
    ) {
      curateAutoSelectRef.current = false;
      onFinishColorChangeHandler(finishColorOptions[0], true);
    }
  }, [finishColorOptions]);

  useEffect(() => {
    const currentLength = finishColorOptions?.length ?? 0;
    const previousLength = previousFinishColorOptionsLengthRef.current;

    if (
      previousLength !== null &&
      previousLength > 0 &&
      currentLength === 0 &&
      showDesignerFinishesWatched &&
      woodOrMaterialWatched &&
      selectedProductLine
    ) {
      const getFinishLabel = () => {
        if (
          selectedProductLine?.name ===
            ProductLineEnums.PRODUCT_LINE_INOVAE2O ||
          selectedProductLine?.name === ProductLineEnums.PRODUCT_LINE_REVOLAE
        )
          return 'Material Color';

        if (selectedProductLine?.name === ProductLineEnums.PRODUCT_LINE_ICS) {
          return 'Material';
        }

        if (
          woodOrMaterialWatched?.label ===
          SpecificationsOptionsEnum.WOOD_TYPE_PAINT
        ) {
          return 'Finish Color';
        }

        return 'Finish Stain';
      };

      const label = getFinishLabel();

      toast.warning(
        `There are no ${label.toLowerCase()} options for selected wood.`,
        {
          autoClose: 5000,
        }
      );

      setValue('showDesignerFinishes', false, { shouldDirty: true });
    }

    previousFinishColorOptionsLengthRef.current = currentLength;
  }, [
    finishColorOptions,
    showDesignerFinishesWatched,
    woodOrMaterialWatched,
    selectedProductLine,
    setValue,
  ]);

  useEffect(() => {
    if (
      finishColorWatched &&
      finishColorOptions &&
      finishColorOptions.length > 0 &&
      woodOrMaterialWatched
    ) {
      const isSelectedFinishInList = finishColorOptions.some(
        (option) =>
          !option.isDisabled &&
          option.value?.toString() === finishColorWatched.value?.toString()
      );

      const filterHasChanged =
        formState.isDirty &&
        UtilService.dirtyOrTouchedFields(formState, [
          'specialFinishSample',
          'showDesignerFinishes',
          'showAvailableFinishesOnly',
        ]);

      const resetKey = `${finishColorWatched.value}-${hasSpecialFinishSample}-${showDesignerFinishesWatched}-${showAvailableFinishesOnlyWatched}`;

      if (
        !isSelectedFinishInList &&
        filterHasChanged &&
        lastResetFinishColorRef.current !== resetKey
      ) {
        lastResetFinishColorRef.current = resetKey;
        finishColorReset(true);
      } else if (isSelectedFinishInList) {
        lastResetFinishColorRef.current = null;
      }
    } else if (!finishColorWatched) {
      lastResetFinishColorRef.current = null;
    }
  }, [
    finishColorOptions,
    finishColorWatched,
    woodOrMaterialWatched,
    formState.isDirty,
    formState.touchedFields,
    hasSpecialFinishSample,
    showDesignerFinishesWatched,
    showAvailableFinishesOnlyWatched,
    setValue,
  ]);

  return isUserCSR ? (
    <>
      {!isPreludeProductLine && (
        <DesignerFinishToggle
          onToggle={handleDesignerFinishToggle}
          hasDesignerFinishes={hasDesignerFinishes}
        />
      )}
      <FormLabel>Finish</FormLabel>
      <Controller
        name="finishColor"
        rules={{ required: !(isUserCSR && isICSProductLine) }}
        control={control}
        render={({ field: { value } }) => (
          <Select
            options={finishColorOptions}
            isDisabled={!finishColorOptions?.length || !canEdit}
            value={value}
            onChange={(val: SelectOptionProps) => {
              onFinishColorChangeHandler(val, true);
            }}
            formatOptionLabel={(option: SelectOptionProps) => (
              <Wrapper flex middle gap={20}>
                <Wrapper flex>
                  <FinishColorImg
                    width="26"
                    height="26"
                    alt={`${option.label}`}
                    src={
                      finishColorOptions?.find(
                        (item: SelectOptionProps) =>
                          item?.value === option?.value
                      )?.imageUrl || noImagePlaceholderSrc
                    }
                  />
                </Wrapper>
                <Wrapper flex>{option.label}</Wrapper>
              </Wrapper>
            )}
          />
        )}
      />
    </>
  ) : (
    <>
      {finishColorOptions === null ? (
        <OrderFinishLabel
          style={{
            productLine: selectedProductLine ?? undefined,
            ...(woodOrMaterialWatched && {
              woodMaterial: {
                id: woodOrMaterialWatched.value,
                name: woodOrMaterialWatched.label,
              },
            }),
          }}
          render={(label) => (
            <P fontSize={14} color={nevada}>
              Please select wood to get {label.toLocaleLowerCase()} options.
            </P>
          )}
        />
      ) : null}

      {(finishColorOptions?.length ?? 0) > 0 ? (
        <Wrapper position="relative">
          {!isPreludeProductLine && (
            <DesignerFinishToggle
              onToggle={handleDesignerFinishToggle}
              hasDesignerFinishes={hasDesignerFinishes}
            />
          )}

          {isFinishColorsLoading && (
            <LoaderFullScreen flex middle center>
              <Loader size={40} />
            </LoaderFullScreen>
          )}

          <ScrollbarsCustom
            autoHeight
            autoHeightMax={350}
            autoHeightMin={50}
            asInput
            ref={(el) => setFinishColorScrollbarsRef(el)}
            hasError={!!formState.errors.finishColor}
          >
            <Controller
              name="finishColor"
              rules={{ required: !(isUserCSR && isICSProductLine) }}
              control={control}
              render={() => (
                <FinishColorContainer>
                  {finishColorOptions?.map((finishColorOption, index) => (
                    <OrderFinishLabel
                      key={finishColorOption.value}
                      style={{
                        productLine: selectedProductLine ?? undefined,
                        ...(woodOrMaterialWatched && {
                          woodMaterial: {
                            id: woodOrMaterialWatched.value,
                            name: woodOrMaterialWatched.label,
                          },
                        }),
                      }}
                      render={(label) => (
                        <WarningTooltip
                          fieldName={label}
                          position={UtilService.calcTooltipPosition({
                            itemPositionIndex: index,
                            numberOfColumns: 5,
                          })}
                          withTooltip={
                            !finishColorOption.isDisabled &&
                            UtilService.shouldShowTooltip(
                              finishColorOptions,
                              finishColorWatched
                            )
                          }
                        >
                          <FinishColorEl
                            className={
                              finishColorOption.value ===
                              finishColorWatched?.value
                                ? 'selected'
                                : ''
                            }
                            onClick={() =>
                              onFinishColorChangeHandler(
                                finishColorOption,
                                true
                              )
                            }
                            disabled={!canEdit}
                          >
                            <FinishColorImg
                              width="96"
                              height="96"
                              alt={`${finishColorOption.label}`}
                              src={
                                finishColorOption.imageUrl ||
                                noImagePlaceholderSrc
                              }
                            />

                            <FinishColorLabel>
                              {finishColorOption.label}
                            </FinishColorLabel>
                          </FinishColorEl>
                        </WarningTooltip>
                      )}
                    />
                  ))}
                </FinishColorContainer>
              )}
            />
          </ScrollbarsCustom>

          <OrderFinishLabel
            style={{
              productLine: selectedProductLine ?? undefined,
              ...(woodOrMaterialWatched && {
                woodMaterial: {
                  id: woodOrMaterialWatched.value,
                  name: woodOrMaterialWatched.label,
                },
              }),
            }}
            render={(label) => (
              <FormError
                label={label}
                validationSchema={{ required: true }}
                error={formState.errors.finishColor}
              />
            )}
          />
        </Wrapper>
      ) : (
        <input
          hidden
          {...register('finishColor', {
            required: !(isUserCSR && isICSProductLine),
          })}
        />
      )}
    </>
  );
};

FinishColor.defaultProps = {
  isFinishColorsLoading: false,
  setIsFinishColorsLoading: undefined,
};

export default FinishColor;
